import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { Action } from '@reduxjs/toolkit';
import {
  ActionButtons,
  addStatusToast,
  VerticalTabPane,
  VerticalTabs,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  DefaultRateDetails,
  saveDefaultRateAsync,
  updateDefaultRateAsync,
} from './defaultRatesSlice';
import DefaultRateWeekdayCheckInCheckOutRestrictions from '../rateRules/checkInCheckOutRestriction/DefaultRateWeekdayCheckInCheckOutRestrictions';
import DefaultRateMinimumLengthOfStay from '../rateRules/minimumLengthOfStay/DefaultRateMinimumLengthOfStay';
import DefaultRatePreparationRestriction from '../rateRules/preparationRestriction/DefaultRatePreparationRestriction';
import DefaultRateShortStay from '../rateRules/shortStay/DefaultRateShortStay';
import DefaultRateLastMinute from '../rateRules/lastMinute/DefaultRateLastMinute';
import DefaultRateWeekdayMinimumStayRestrictions from '../rateRules/weekdayMinimumStay/DefaultRateWeekdayMinimumStayRestrictions';
import DefaultRateWeekdayPriceChangeRules from '../rateRules/weekdayPriceChange/DefaultRateWeekdayPriceChangeRules';
import DefaultRateWeekdayCheckInCheckOutTimes from '../rateRules/weekdayCheckInCheckOutTime/DefaultRateWeekdayCheckInCheckOutTimes';
import {
  copyCheckInCheckOutTimesToSeasons,
  copyPricesToSeasons,
} from '../defaultRateTools/defaultRateToolsSlice';
import SeasonRatePickerContainer from '../seasonRates/SeasonRatePickerContainer';
import DefaultRatePriceChange from '../rateRules/priceChange/DefaultRatePriceChange';
import DefaultRateReleaseInterval from '../rateRules/releaseInterval/DefaultRateReleaseInterval';
import DefaultRateAddonServiceRules from '../rateRules/addonServiceRules/DefaultRateAddonServiceRules';
import { clearInputErrors, selectInputErrors } from '../errors/errorsSlice';
import {
  saveMunicipalityAsync,
  updateMunicipalityAsync,
} from '../municipalities/municipalitiesSlice';
import InputErrorsDisplay from '../errors/InputErrorsDisplay';

function AddOrUpdateDefaultRate({
  defaultRateItem,
  isAddMode,
  hasWriteAccess,
}: {
  defaultRateItem: DefaultRateDetails;
  /* TODO Replace any/ CORE-1006 */
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  isAddMode: any;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const inputErrors = useAppSelector(selectInputErrors);

  const Tabs: string[] = [
    'Perustiedot ja hinnat',
    'Kohteen varoaika',
    'Varauksen minimikesto',
    'Sisään- ja uloskirjautumisrajoitukset viikonpäiville',
    'Lyhyen varauksen hinnanmuutos',
    'Hintakauden yleinen hinnan muutos',
    'Viime hetken varauksen hinnan muutos',
    'Varauksen vähimmäiskesto viikonpäiville',
    'Viikonpäiväkohtainen hinnoittelu',
    'Viikonpäiväkohtaiset sisään- ja uloskirjautumisajat',
    'Varattavissa rajoituksetta',
    'Lisäpalvelusäännöt',
  ];

  const [defaultRate, setDefaultRate] = useState(defaultRateItem);
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(Tabs[0]);
  const [
    copyCheckInCheckOutTimesStartDate,
    setCopyCheckInCheckOutTimesStartDate,
  ] = useState<Date | null>(null);
  const [copyCheckInCheckOutTimesEndDate, setCopyCheckInCheckOutTimesEndDate] =
    useState<Date | null>(null);
  const [copiedCheckInCheckOutTimes, setCopiedCheckInCheckOutTimes] =
    useState<boolean>(false);
  const [copyPricesStartDate, setCopyPricesStartDate] = useState<Date | null>(
    null,
  );
  const [copyPricesEndDate, setCopyPricesEndDate] = useState<Date | null>(null);
  const [copiedPrices, setCopiedPrices] = useState<boolean>(false);
  const [
    selectedSeasonRateIdsForCheckInCheckOutTimes,
    setSelectedSeasonRateIdsForCheckInCheckOutTimes,
  ] = useState<string[]>([]);
  const [selectedSeasonRateIdsForPrices, setSelectedSeasonRateIdsForPrices] =
    useState<string[]>([]);

  useEffect(() => {
    dispatch(clearInputErrors());
  }, [dispatch]);

  useEffect(() => {
    setDefaultRate(defaultRateItem);
    setSubmitted(false);
  }, [defaultRateItem]);

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newDefaultRate = { ...defaultRate };
    let pointer = newDefaultRate;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setDefaultRate(newDefaultRate);
  };

  const handleNumericInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const path = name.split('.');
    const finalProp = path.pop();
    const newDefaultRate = { ...defaultRate };
    let pointer = newDefaultRate;

    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });

    pointer[finalProp] = value !== '' ? parseFloat(value) : 0;
    setDefaultRate(newDefaultRate);
  };

  const handleRatePrices = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const { duration } = event.target.dataset;
    const textValue = event.target.value;
    const newDefaultRate = { ...defaultRate };

    newDefaultRate[finalProp] = defaultRate[finalProp].map(item => {
      if (item.duration.toString() === duration.toString()) {
        return { ...item, dailyPrice: textValue !== '' ? textValue : 0 };
      }
      return item;
    });
    setDefaultRate(newDefaultRate);
  };

  const addOrUpdateDefaultRate = async () => {
    let actionResult: Action;
    if (isAddMode) {
      actionResult = await dispatch(saveDefaultRateAsync(defaultRate));
    } else {
      actionResult = await dispatch(updateDefaultRateAsync(defaultRate));
    }

    if (
      saveDefaultRateAsync.fulfilled.match(actionResult) ||
      updateDefaultRateAsync.fulfilled.match(actionResult)
    ) {
      dispatch(
        addStatusToast({
          Message: `Tiedot on tallennettu.`,
        }),
      );
      setSubmitted(true);
      dispatch(clearInputErrors());
    }
  };

  const handleCopyingCheckInCheckOutTimesForSeasonRates = async () => {
    if (selectedSeasonRateIdsForCheckInCheckOutTimes.length > 0) {
      dispatch(
        copyCheckInCheckOutTimesToSeasons({
          accommodationId: defaultRate.accommodationId,
          selectedSeasonRateIds: selectedSeasonRateIdsForCheckInCheckOutTimes,
          checkInTime: defaultRate.checkInTime,
          checkOutTime: defaultRate.checkOutTime,
        }),
      )
        .unwrap()
        .then(() => {
          setCopiedCheckInCheckOutTimes(true);
        });
    }
  };

  const handleCopyingPricesForSeasonRates = async () => {
    if (selectedSeasonRateIdsForPrices) {
      dispatch(
        copyPricesToSeasons({
          accommodationId: defaultRate.accommodationId,
          selectedSeasonRateIds: selectedSeasonRateIdsForPrices,
          priceForAdditionalDay: defaultRate.priceForAdditionalDay,
          commissionPercent: defaultRate.commissionPercent,
          ratePrices: defaultRate.ratePrices,
        }),
      )
        .unwrap()
        .then(() => {
          setCopiedPrices(true);
        });
    }
  };

  const handleSeasonRateIdsForCheckInCheckOutTimes = (
    selectedIds: string[],
  ) => {
    setSelectedSeasonRateIdsForCheckInCheckOutTimes(selectedIds);
  };

  const handleSeasonRateIdsForPrices = (selectedIds: string[]) => {
    setSelectedSeasonRateIdsForPrices(selectedIds);
  };

  const renderRatePriceItems = defaultRate.ratePrices?.map(item => (
    <Col key={item.duration}>
      <Form.Group controlId="ratePrices" className="mb-24">
        <Form.Label>{item.duration} vrk</Form.Label>
        <Form.Control
          type="text"
          name="ratePrices"
          data-duration={item.duration}
          onChange={handleRatePrices}
          value={item.dailyPrice || ''}
          readOnly={!hasWriteAccess}
        />
        <Form.Text muted>Ostohinta: {item.dailyPurchasePrice}</Form.Text>
      </Form.Group>
    </Col>
  ));

  return (
    <VerticalTabs
      ActiveTab={activeTab}
      ChangeTab={setActiveTab}
      TabContainerId="updateDefaultRateTabs"
      Tabs={Tabs}
    >
      <VerticalTabPane IsActive={activeTab === Tabs[0]} Name={Tabs[0]}>
        <h4 className="h5 mb-24">Sisään- ja uloskirjautuminen</h4>
        <Row sm={1}>
          <Row sm={1}>
            <Col>
              <Form.Label>Valitse kohdekaudet</Form.Label>
            </Col>
          </Row>
          <Col>
            <SeasonRatePickerContainer
              selectedAccommodationId={defaultRate.accommodationId}
              onSelectedIdsChange={handleSeasonRateIdsForCheckInCheckOutTimes}
            />
          </Col>
        </Row>
        <Row sm={2}>
          <Col>
            <Form.Group controlId="checkInTime" className="mb-24">
              <Form.Label>Sisäänkirjautumisen kellonaika</Form.Label>
              <Form.Control
                type="text"
                name="checkInTime"
                onChange={handleInputChange}
                value={defaultRate.checkInTime}
                placeholder="Anna sisäänkirjautumisen kellonaika muodossa HH:mm (esim. 12:30)"
                readOnly={!hasWriteAccess}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="checkOutTime" className="mb-24">
              <Form.Label>Uloskirjautumisen kellonaika</Form.Label>
              <Form.Control
                type="text"
                name="checkOutTime"
                onChange={handleInputChange}
                value={defaultRate.checkOutTime}
                placeholder="Anna uloskirjautumisen kellonaika muodossa HH:mm (esim. 12:30)"
                readOnly={!hasWriteAccess}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row sm={1}>
          <Col>
            <Form.Group className="mb-24">
              {/*
              <Form.Label>Kopioinnin alkupäivämäärä</Form.Label>
              <div className="w-100 position-relative">
                <DatePicker locale={fi}
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  onChange={date => setCopyCheckInCheckOutTimesStartDate(date)}
                  className="form-control"
                  selected={copyCheckInCheckOutTimesStartDate}
                />
                <Button
                  variant="link"
                  className="pe-none position-absolute end-0 top-0"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event" />
                </Button>
              </div>
              <Form.Label>Kopioinnin päättymispäivämäärä</Form.Label>
              <div className="w-100 position-relative">
                <DatePicker locale={fi}
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  onChange={date => setCopyCheckInCheckOutTimesEndDate(date)}
                  className="form-control"
                  selected={copyCheckInCheckOutTimesEndDate}
                />
                <Button
                  variant="link"
                  className="pe-none position-absolute end-0 top-0"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event" />
                </Button>
              </div>
              <Form.Text muted>
                Kohdistuu kausiin joiden alkupäivä on ennen kopioinnin
                päättymispäivämäärää sekä loppupäivä on kopioinnin
                alkamispäivämäärän jälkeen.
              </Form.Text>
              */}
              <Button
                variant="outline-blue"
                className="mt-16 mb-16 w-100"
                onClick={handleCopyingCheckInCheckOutTimesForSeasonRates}
              >
                Kopioi sisään- ja uloskirjautumisajat hintakausille
              </Button>
              {copiedCheckInCheckOutTimes && (
                <div className="mt-2 pt-2">
                  <Alert variant="success">
                    Sisään- ja uloskirjautumisajat kopioitu hintakausille!
                  </Alert>
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <hr className="hr-light mb-32" />

        <Row sm={1} className="mb-24">
          <Col>
            <Row sm={1}>
              <Col>
                <Form.Label>Valitse kohdekaudet</Form.Label>
              </Col>
            </Row>
            <SeasonRatePickerContainer
              selectedAccommodationId={defaultRate.accommodationId}
              onSelectedIdsChange={handleSeasonRateIdsForPrices}
            />
          </Col>
        </Row>

        <Row sm={2}>
          <Col>
            <Form.Group controlId="commissionPercent" className="mb-24">
              <Form.Label>Provisioprosentti</Form.Label>
              <Form.Control
                type="text"
                name="commissionPercent"
                onChange={handleNumericInput}
                value={defaultRate.commissionPercent || ''}
                placeholder="Anna provisioprosentti"
                readOnly={!hasWriteAccess}
              />
            </Form.Group>
          </Col>
        </Row>

        <h4 className="h5 mb-24">Hinnat keston mukaan</h4>
        <Row sm={2} xl={3}>
          {renderRatePriceItems}
        </Row>
        <Row sm={2}>
          <Col>
            <Form.Group className="mb-24">
              <Form.Label>Lisävuorokauden hinta</Form.Label>
              <Form.Control
                type="text"
                name="priceForAdditionalDay"
                placeholder="Lisävuorokauden hinta"
                onChange={handleNumericInput}
                value={defaultRate.priceForAdditionalDay || ''}
                required
                readOnly={!hasWriteAccess}
              />
              <Form.Text muted>
                Käytetään päivähintana yli 7 vuorokauden varauksiin. Hinta
                lisätään jokaiselle vuorokaudelle joka ylittää 7 vuorokautta.
                Esim 10 vuorokauden varauksessa hinta on 7 vuorokauden hinta + 3
                x lisävuorokauden hinta.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
        <Row sm={1}>
          <Col>
            <Form.Group className="mb-24">
              {/*
              <Form.Label>Kopioinnin alkupäivämäärä</Form.Label>
              <div className="w-100 position-relative">
                <DatePicker locale={fi}
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  onChange={date => setCopyPricesStartDate(date)}
                  className="form-control"
                  selected={copyPricesStartDate}
                />
                <Button
                  variant="link"
                  className="pe-none position-absolute end-0 top-0"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event" />
                </Button>
              </div>
              <Form.Label>Kopioinnin päättymispäivämäärä</Form.Label>
              <div className="w-100 position-relative">
                <DatePicker locale={fi}
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  onChange={date => setCopyPricesEndDate(date)}
                  className="form-control"
                  selected={copyPricesEndDate}
                />
                <Button
                  variant="link"
                  className="pe-none position-absolute end-0 top-0"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event" />
                </Button>
              </div>
              <Form.Text muted>
                Kohdistuu kausiin joiden alkupäivä on ennen kopioinnin
                päättymispäivämäärää sekä loppupäivä on kopioinnin
                alkamispäivämäärän jälkeen.
              </Form.Text>
              */}
              <Button
                variant="outline-blue"
                className="mt-16 mb-16 w-100"
                onClick={handleCopyingPricesForSeasonRates}
              >
                Kopioi hinnat hintakausille
              </Button>
              {copiedPrices && (
                <div className="mt-2 pt-2">
                  <Alert variant="success">
                    Hinnat kopioitu hintakausille!
                  </Alert>
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <InputErrorsDisplay inputErrors={inputErrors} />

        <ActionButtons
          HandleSave={addOrUpdateDefaultRate}
          HideSaveButton={!hasWriteAccess}
          HideCancelButton
          DeleteButtonText=""
        />
      </VerticalTabPane>

      {defaultRate.id && (
        <>
          <VerticalTabPane IsActive={activeTab === Tabs[1]} Name={Tabs[1]}>
            <DefaultRatePreparationRestriction
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[2]} Name={Tabs[2]}>
            <DefaultRateMinimumLengthOfStay
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[3]} Name={Tabs[3]}>
            <DefaultRateWeekdayCheckInCheckOutRestrictions
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[4]} Name={Tabs[4]}>
            <DefaultRateShortStay
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[5]} Name={Tabs[5]}>
            <DefaultRatePriceChange
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[6]} Name={Tabs[6]}>
            <DefaultRateLastMinute
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[7]} Name={Tabs[7]}>
            <DefaultRateWeekdayMinimumStayRestrictions
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[8]} Name={Tabs[8]}>
            <DefaultRateWeekdayPriceChangeRules
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[9]} Name={Tabs[9]}>
            <DefaultRateWeekdayCheckInCheckOutTimes
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[10]} Name={Tabs[10]}>
            <DefaultRateReleaseInterval
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
          <VerticalTabPane IsActive={activeTab === Tabs[11]} Name={Tabs[11]}>
            <DefaultRateAddonServiceRules
              defaultRateId={defaultRate.id}
              accommodationId={defaultRate.accommodationId}
              hasWriteAccess={hasWriteAccess}
            />
          </VerticalTabPane>
        </>
      )}
    </VerticalTabs>
  );
}

export default AddOrUpdateDefaultRate;
